import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, get, put } from '../api';
import { askConfirm } from '../components/ask';
import { Field, Problems } from '../components/Field';
import { toast } from '../components/toast';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { renderPreview, smsSegments } from '../templates';

interface Text {
  subject: string;
  body: string;
}

interface TemplateDetail {
  key: string;
  label: string;
  description: string;
  tags: { key: string; label: string; sample: string }[];
  email: Text & { customised: boolean; default: Text };
  sms: { body: string; customised: boolean; default: { body: string } } | null;
}

type Channel = 'email' | 'sms';

/** One message's wording, by email and SMS, with tags to drop in and a live preview. */
export function TemplateEditor() {
  const { trigger = '' } = useParams();
  const [tick, setTick] = useState(0);
  const loaded = useLoad(
    () => get<TemplateDetail>(`/api/templates/${encodeURIComponent(trigger)}`),
    [trigger, tick],
  );
  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  if (loaded.error || !loaded.data) {
    return (
      <p role="alert" className="error">
        {loaded.error ?? 'That message does not exist.'}
      </p>
    );
  }
  const d = loaded.data;
  // Remount when what is stored changes (a save or a reset), so the form starts from it.
  const stored = JSON.stringify([d.email.subject, d.email.body, d.sms?.body ?? '']);
  return <Editor key={stored} detail={d} reload={() => setTick((t) => t + 1)} />;
}

function Editor(props: { detail: TemplateDetail; reload(): void }) {
  const d = props.detail;
  const [channel, setChannel] = useState<Channel>('email');
  const stored: Record<Channel, Text> = {
    email: { subject: d.email.subject, body: d.email.body },
    sms: { subject: '', body: d.sms?.body ?? '' },
  };
  const [draft, setDraft] = useState<Record<Channel, Text>>(stored);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastField = useRef<'subject' | 'body'>('body');

  const text = draft[channel];
  const dirty = (['email', 'sms'] as Channel[]).some(
    (c) => draft[c].subject !== stored[c].subject || draft[c].body !== stored[c].body,
  );
  const customised = channel === 'email' ? d.email.customised : (d.sms?.customised ?? false);
  const samples = Object.fromEntries(d.tags.map((t) => [t.key, t.sample]));
  const set = (patch: Partial<Text>) =>
    setDraft((all) => ({ ...all, [channel]: { ...all[channel], ...patch } }));

  /** Put `{{tag}}` where the cursor is in the field last used, and leave the cursor after it. */
  function insert(tag: string) {
    const which = channel === 'sms' ? 'body' : lastField.current;
    const el = which === 'subject' ? subjectRef.current : bodyRef.current;
    const value = text[which];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const token = `{{${tag}}}`;
    set({ [which]: value.slice(0, start) + token + value.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  async function save() {
    setProblems([]);
    setFields({});
    setSaving(true);
    try {
      await put(`/api/templates/${d.key}/${channel}`, {
        ...(channel === 'email' ? { subject: text.subject } : {}),
        body: text.body,
      });
      toast('Saved.');
      props.reload();
    } catch (err) {
      const e = describe(err);
      setProblems(e.problems);
      setFields(e.fields);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    const ok = await askConfirm({
      title: 'Reset to the default wording?',
      body: `Your changes to the ${channel === 'email' ? 'email' : 'SMS'} for ${d.label} will be replaced.`,
      confirmLabel: 'Reset',
    });
    if (!ok) return;
    try {
      await api('DELETE', `/api/templates/${d.key}/${channel}`);
      toast('Reset to the default.');
      props.reload();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  const count = channel === 'sms' ? smsSegments(text.body) : null;

  return (
    <>
      <UnsavedChanges when={dirty && !saving} />
      <p>
        <Link to="/templates">← Templates</Link>
      </p>
      <h1>{d.label}</h1>
      {d.description && <p className="muted">{d.description}</p>}

      {d.sms && (
        <div role="tablist" aria-label="Channel" className="sections">
          {(['email', 'sms'] as Channel[]).map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={channel === c}
              className={channel === c ? 'on' : undefined}
              onClick={() => {
                setChannel(c);
                setProblems([]);
                setFields({});
              }}
            >
              {c === 'email' ? 'Email' : 'SMS'}
            </button>
          ))}
        </div>
      )}

      <div className="template-editor">
        <section className="stack" aria-label="Wording">
          <Problems problems={problems} />
          {channel === 'email' && (
            <Field label="Subject" error={fields.subject}>
              <input
                ref={subjectRef}
                value={text.subject}
                onFocus={() => (lastField.current = 'subject')}
                onChange={(e) => set({ subject: e.target.value })}
              />
            </Field>
          )}
          <Field
            label="Message"
            error={fields.body}
            hint={count ? `${count.chars} characters · ${count.segments} SMS` : undefined}
          >
            <textarea
              ref={bodyRef}
              rows={channel === 'sms' ? 5 : 12}
              value={text.body}
              onFocus={() => (lastField.current = 'body')}
              onChange={(e) => set({ body: e.target.value })}
            />
          </Field>
          <div className="tag-strip" aria-label="Insert a tag" role="group">
            {d.tags.map((t) => (
              <button
                key={t.key}
                type="button"
                className="chip"
                title={`{{${t.key}}}`}
                // Keep the cursor in the field: inserting happens where it is.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="row start">
            <button type="button" disabled={saving} onClick={() => void save()}>
              Save template
            </button>
            {customised && (
              <button type="button" className="quiet" onClick={() => void reset()}>
                Reset to default
              </button>
            )}
          </div>
        </section>

        <section className="template-preview" aria-label="Preview">
          <h2>Preview</h2>
          {channel === 'email' && (
            <p>
              <strong>{renderPreview(text.subject, samples)}</strong>
            </p>
          )}
          <p className="preview-body">{renderPreview(text.body, samples)}</p>
        </section>
      </div>
    </>
  );
}
